import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import Select from "react-select";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";

/**
 * Provider Information Tab — per spec Section 3.2.
 *
 * Section 1 – Basic Information
 *   Read-only:  PECO Certified, NPI Number, License Number, Associated Facilities
 *   Editable:   Provider Credentials, Phone, Fax, Email,
 *               Alternate Signee 1/2 (searchable PECOS-certified physician dropdown)
 *
 * Section 2 – Address Information
 *   Editable:   Address Line 1/2, City, State, Zip Code
 *
 * Section 3 – Provider Signee Information
 *   Editable:   Provider POC (ContactMethod from DB), Preferred Delivery Method (from DB),
 *               Provider Signee Name, Email, Phone Number
 */
function ProviderInfoTab({ physicianId }) {
  const fieldsetClass = "p-2 mb-2 bg-light border border-dark rounded";
  const labelClass = "form-label mb-1";
  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Dropdown option lists (loaded once on mount)
  const [physicianOptions,      setPhysicianOptions]      = useState([]);
  const [deliveryMethodOptions,  setDeliveryMethodOptions]  = useState([]);
  const [contactMethodOptions,   setContactMethodOptions]   = useState([]);

  // Editable fields — Section 1
  const [providerCredentials, setProviderCredentials] = useState("");
  const [providerPhone,       setProviderPhone]       = useState("");
  const [providerFax,         setProviderFax]         = useState("");
  const [providerEmail,       setProviderEmail]       = useState("");
  // Stored as number | null
  const [alternateSignee1,    setAlternateSignee1]    = useState(null);
  const [alternateSignee2,    setAlternateSignee2]    = useState(null);

  // Section 2 — Address
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city,         setCity]         = useState("");
  const [stateAbbr,    setStateAbbr]    = useState("");
  const [zipCode,      setZipCode]      = useState("");

  // Section 3 — Signee
  const [contactMethodId,           setContactMethodId]           = useState("");
  const [preferredDeliveryMethodId, setPreferredDeliveryMethodId] = useState("");
  const [signeeName,  setSigneeName]  = useState("");
  const [signeeEmail, setSigneeEmail] = useState("");
  const [signeePhone, setSigneePhone] = useState("");

  // Load lookup lists on mount
  useEffect(() => {
    Api.getRequest(Api.Uri_CmnLookupPhysicians)
      .then((res) => {
        if (res.data.statusCode === 200 && Array.isArray(res.data.result)) {
          setPhysicianOptions(res.data.result.map((p) => ({ value: p.id, label: p.name })));
        }
      })
      .catch((ex) => Common.LogError(ex, "ProviderInfoTab", "loadPhysicians"));

    Api.getRequest(Api.Uri_CmnLookupDeliveryMethods)
      .then((res) => {
        if (res.data.statusCode === 200 && Array.isArray(res.data.result)) {
          setDeliveryMethodOptions(res.data.result.map((m) => ({ value: String(m.id), label: m.name })));
        }
      })
      .catch((ex) => Common.LogError(ex, "ProviderInfoTab", "loadDeliveryMethods"));

    Api.getRequest(Api.Uri_CmnLookupContactMethods)
      .then((res) => {
        if (res.data.statusCode === 200 && Array.isArray(res.data.result)) {
          setContactMethodOptions(res.data.result.map((m) => ({ value: String(m.id), label: m.name })));
        }
      })
      .catch((ex) => Common.LogError(ex, "ProviderInfoTab", "loadContactMethods"));
  }, []);

  // Load provider info when physicianId changes
  useEffect(() => {
    if (!physicianId) return;
    setIsLoading(true);
    Api.getRequest(Api.Uri_CmnProviderInfo + physicianId)
      .then((res) => {
        if (res.data.statusCode === 200 && res.data.result) {
          const d = res.data.result;
          setInfo(d);
          setProviderCredentials(d.providerCredentials || "");
          setProviderPhone(d.providerPhoneNumber || "");
          setProviderFax(d.providerFaxNumber || "");
          setProviderEmail(d.providerEmail || "");
          setAlternateSignee1(d.alternateSignee1 != null ? d.alternateSignee1 : null);
          setAlternateSignee2(d.alternateSignee2 != null ? d.alternateSignee2 : null);
          setAddressLine1(d.addressLine1 || "");
          setAddressLine2(d.addressLine2 || "");
          setCity(d.city || "");
          setStateAbbr(d.stateAbbreviation || "");
          setZipCode(d.zipCode || "");
          setContactMethodId(d.contactMethodId != null ? String(d.contactMethodId) : "");
          setPreferredDeliveryMethodId(d.preferredDeliveryMethodId != null ? String(d.preferredDeliveryMethodId) : "");
          setSigneeName(d.providerSigneeName || "");
          setSigneeEmail(d.providerSigneeEmail || "");
          setSigneePhone(d.providerSigneePhoneNumber || "");
        }
      })
      .catch((ex) => Common.LogError(ex, "ProviderInfoTab", "loadProviderInfo"))
      .finally(() => setIsLoading(false));
  }, [physicianId]);

  const handleSave = () => {
    setIsSaving(true);
    const payload = {
      providerCredentials:       providerCredentials       || null,
      providerPhoneNumber:       providerPhone             || null,
      providerFaxNumber:         providerFax               || null,
      providerEmail:             providerEmail             || null,
      // 0 = clear signee (API convention); positive int = PhysicianID
      alternateSignee1: alternateSignee1 != null ? Number(alternateSignee1) : 0,
      alternateSignee2: alternateSignee2 != null ? Number(alternateSignee2) : 0,
      addressLine1:              addressLine1              || null,
      addressLine2:              addressLine2              || null,
      city:                      city                      || null,
      stateAbbreviation:         stateAbbr                 || null,
      zipCode:                   zipCode                   || null,
      contactMethodId:           contactMethodId           ? Number(contactMethodId)           : null,
      preferredDeliveryMethodId: preferredDeliveryMethodId ? Number(preferredDeliveryMethodId) : null,
      providerSigneeName:        signeeName                || null,
      providerSigneeEmail:       signeeEmail               || null,
      providerSigneePhoneNumber: signeePhone               || null,
    };
    Api.patchRequest(Api.Uri_CmnProviderInfo + physicianId, payload)
      .then((res) => {
        if (res.data.success) {
          Common.showSuccessAlertAutoClose("Provider information updated successfully.", "success", "Success!", 3000);
        } else {
          Common.showSuccessAlert(res.data.message || "Failed to update provider information.", "warning", "Warning!", 3000);
        }
      })
      .catch((ex) => {
        Common.LogError(ex, "ProviderInfoTab", "handleSave");
        Common.showSuccessAlertAutoClose("An error occurred while saving.", "warning", "Failed!", 3000);
      })
      .finally(() => setIsSaving(false));
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!info) return null;

  // react-select options matched to current alternate signee IDs
  const signee1Option = alternateSignee1 != null
    ? physicianOptions.find((o) => o.value === alternateSignee1) || null
    : null;
  const signee2Option = alternateSignee2 != null
    ? physicianOptions.find((o) => o.value === alternateSignee2) || null
    : null;

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: "31px", fontSize: "0.875rem" }),
    menu:    (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="p-2">
      <fieldset className={fieldsetClass}>
        <legend className="text-primary fw-500 w-auto px-1">Provider information</legend>
        <Row className="g-2">
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>PECO certified</Form.Label>
              <Form.Control
                size="sm"
                readOnly
                value={info.pecosCertified === true ? "Yes" : info.pecosCertified === false ? "No" : "--"}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>NPI number</Form.Label>
              <Form.Control size="sm" readOnly value={Common.replaceNullOrEmptyWithString(info.npiNumber, "--")} />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>License number</Form.Label>
              <Form.Control size="sm" readOnly value={Common.replaceNullOrEmptyWithString(info.licenseNumber, "--")} />
            </Form.Group>
          </Col>
          <Col xs={12}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Associated facilities</Form.Label>
              <Form.Control
                size="sm"
                readOnly
                value={
                  info.associatedFacilities && info.associatedFacilities.length > 0
                    ? info.associatedFacilities.map((f) => f.facilityName).join(", ")
                    : "No associated facilities."
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider credentials</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="e.g. MD, DO, NP"
                value={providerCredentials}
                onChange={(e) => setProviderCredentials(e.target.value)}
                maxLength={100}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider phone number</Form.Label>
              <Form.Control
                size="sm"
                type="tel"
                placeholder="Phone number"
                value={providerPhone}
                onChange={(e) => setProviderPhone(e.target.value)}
                maxLength={50}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider fax number</Form.Label>
              <Form.Control
                size="sm"
                type="tel"
                placeholder="Fax number"
                value={providerFax}
                onChange={(e) => setProviderFax(e.target.value)}
                maxLength={50}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider email</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                placeholder="Email address"
                value={providerEmail}
                onChange={(e) => setProviderEmail(e.target.value)}
                maxLength={200}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Alternate signee 1</Form.Label>
              <Select
                options={physicianOptions}
                value={signee1Option}
                onChange={(opt) => setAlternateSignee1(opt ? opt.value : null)}
                isClearable
                placeholder="Search PECOS physician..."
                noOptionsMessage={() => "No PECOS-certified physicians found"}
                styles={selectStyles}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Alternate signee 2</Form.Label>
              <Select
                options={physicianOptions}
                value={signee2Option}
                onChange={(opt) => setAlternateSignee2(opt ? opt.value : null)}
                isClearable
                placeholder="Search PECOS physician..."
                noOptionsMessage={() => "No PECOS-certified physicians found"}
                styles={selectStyles}
              />
            </Form.Group>
          </Col>
        </Row>
      </fieldset>

      <fieldset className={fieldsetClass}>
        <legend className="text-primary fw-500 w-auto px-1">Address information</legend>
        <Row className="g-2">
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Address line 1</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Address line 1"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                maxLength={200}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Address line 2</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Address line 2"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                maxLength={200}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>City</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={100}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>State</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="State abbreviation"
                value={stateAbbr}
                onChange={(e) => setStateAbbr(e.target.value)}
                maxLength={10}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Zip code</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Zip code"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                maxLength={20}
              />
            </Form.Group>
          </Col>
        </Row>
      </fieldset>

      <fieldset className={fieldsetClass}>
        <legend className="text-primary fw-500 w-auto px-1">Provider signee information</legend>
        <Row className="g-2">
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider point of contact (POC)</Form.Label>
              <Form.Control size="sm" as="select" value={contactMethodId} onChange={(e) => setContactMethodId(e.target.value)}>
                <option value="">-- Select contact method --</option>
                {contactMethodOptions.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Preferred delivery method</Form.Label>
              <Form.Control
                size="sm"
                as="select"
                value={preferredDeliveryMethodId}
                onChange={(e) => setPreferredDeliveryMethodId(e.target.value)}
              >
                <option value="">-- Select --</option>
                {deliveryMethodOptions.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider signee name</Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Full name of signee"
                value={signeeName}
                onChange={(e) => setSigneeName(e.target.value)}
                maxLength={255}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider signee email</Form.Label>
              <Form.Control
                size="sm"
                type="email"
                placeholder="Signee email"
                value={signeeEmail}
                onChange={(e) => setSigneeEmail(e.target.value)}
                maxLength={200}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group className="mb-2">
              <Form.Label className={labelClass}>Provider signee phone number</Form.Label>
              <Form.Control
                size="sm"
                type="tel"
                placeholder="Signee phone"
                value={signeePhone}
                onChange={(e) => setSigneePhone(e.target.value)}
                maxLength={15}
              />
            </Form.Group>
          </Col>
        </Row>
      </fieldset>

      <div className="d-flex justify-content-end mt-2">
        <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

export default ProviderInfoTab;
